import { Box, Text } from '@nimbus-ds/components';
import OrderInvoiceResponseDto from '@tiendanube/common/src/domains/orders/commons/OrderInvoiceResponseDto';
import InvoiceOperationEnum from '@tiendanube/common/src/enums/InvoiceOperation';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import InvoiceSection from '../InvoiceSection';
import OptionInvoicePopover from '../OptionInvoicePopover';

interface InvoicesProps {
  readonly invoices: OrderInvoiceResponseDto[];
  readonly t: ReturnType<typeof useTranslationOrders>;
  onOpenInvoiceOperationModal: (
    operation: InvoiceOperationEnum,
    number: string,
    reason?: string,
  ) => void;
  readonly isEmissionInProgress: boolean;
}

function Invoices({
  invoices,
  t,
  onOpenInvoiceOperationModal,
  isEmissionInProgress,
}: Readonly<InvoicesProps>) {
  if (!invoices?.length) return null;

  const hasErrors = (event?: { error_message?: string[] }) =>
    (event?.error_message?.length ?? 0) > 0;

  const sortedInvoices = [...invoices].sort((a, b) => b.id - a.id);

  return (
    <Box>
      {sortedInvoices.map((invoice, index) => {
        const {
          id,
          invoice_number,
          xml_url,
          pdf_url,
          last_correction_event,
          cancellation_event,
        } = invoice;

        const isCancelledSuccessfully =
          !!cancellation_event && !hasErrors(cancellation_event);
        const showCorrection =
          last_correction_event?.event_type === InvoiceOperationEnum.CORRECTION;
        const showCancellation =
          cancellation_event?.event_type === InvoiceOperationEnum.CANCELLATION;

        return (
          <Box
            key={id}
            paddingY="2"
            borderTopWidth={index > 0 ? '1' : undefined}
            borderStyle="solid"
            borderColor="neutral-surfaceDisabled"
          >
            <InvoiceSection
              title={t('cardInvoice.invoice.succes', { key: invoice_number })}
              xmlUrl={xml_url}
              pdfUrl={isCancelledSuccessfully ? undefined : pdf_url}
              t={t}
            />

            {showCorrection && (
              <InvoiceSection
                title={t('cardInvoice.invoice.edit')}
                errorTitle={t('cardInvoice.alert.edit.title')}
                xmlUrl={
                  hasErrors(last_correction_event)
                    ? undefined
                    : last_correction_event?.xml_url
                }
                pdfUrl={
                  hasErrors(last_correction_event)
                    ? undefined
                    : last_correction_event?.pdf_url
                }
                errorMessage={last_correction_event?.error_message}
                t={t}
              />
            )}

            {showCancellation && (
              <InvoiceSection
                title={t('cardInvoice.invoice.cancel')}
                errorTitle={t('cardInvoice.alert.cancel.title')}
                xmlUrl={
                  hasErrors(cancellation_event)
                    ? undefined
                    : cancellation_event?.xml_url
                }
                pdfUrl={
                  hasErrors(cancellation_event)
                    ? undefined
                    : cancellation_event?.pdf_url
                }
                errorMessage={cancellation_event?.error_message}
                t={t}
              />
            )}

            {!isCancelledSuccessfully && (
              <OptionInvoicePopover
                onOpenModal={onOpenInvoiceOperationModal}
                invoice={invoice}
                t={t}
              />
            )}
          </Box>
        );
      })}

      {isEmissionInProgress && (
        <Box
          key="inprogress"
          paddingY="2"
          borderTopWidth={sortedInvoices.length > 0 ? '1' : undefined}
          borderStyle="solid"
          borderColor="neutral-surfaceDisabled"
        >
          <Text fontSize="base" color="neutral-textLow">
            {t('cardInvoice.issued')}
          </Text>
        </Box>
      )}
    </Box>
  );
}

export default Invoices;
